"use client";
import { VISIT_REASON_PATHS } from "@/constants";
import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import { resolveVisitReasonFromSlug } from "@/lib/utils/resolve-visit-reason-from-slug";

const KNOWN_REASON_SLUGS = new Set(Object.values(VISIT_REASON_PATHS).flat());
// The public day-of-service experience is its own deployment, mounted one level below the public
// scheduler, so only there do the API routes live under `<base>/dayofservice/api/...`. Every other
// mount (preview environments such as `/schedule-an-appointment-2`, local dev) serves a single
// deployment whose API routes sit directly under the base path. Keep this in sync with the CDN
// behavior/routing config — a rename on either side silently sends requests to the wrong origin.
const PUBLIC_SCHEDULER_BASE_PATH = "/schedule-an-appointment";
const DAY_OF_SERVICE_API_SEGMENT = "dayofservice";

const getPathSegments = (pathname: string): string[] =>
  pathname.split(/[?#]/u)[0].split("/").filter(Boolean);

/**
 * The prefix the app is mounted under. Not for building API URLs — use {@link getApiBasePath},
 * which additionally accounts for the separately deployed day-of-service mount.
 */
export const getBasePath = (): string => {
  if (globalThis.window) {
    const [firstSegment = ""] = getPathSegments(globalThis.window.location.pathname);
    if (!firstSegment) return "";
    // A reason slug at the root means the app is mounted at "/" (e.g. localhost),
    // not under a deployment prefix.
    if (KNOWN_REASON_SLUGS.has(firstSegment.toLowerCase())) return "";
    return `/${firstSegment}`;
  }

  throw new Error("getBasePath can only be called in a browser environment");
};

/**
 * Healthy Mouth Movement is the day-of-service reason, and the /dayofservice slug pre-selects it,
 * so the reason the URL resolves to — rather than a hard-coded prefix — is what identifies the
 * route. Any segment is considered so this stays correct both under a deployment prefix
 * (`/<prefix>/dayofservice`) and on deeper routes (`/<prefix>/dayofservice/<step>`).
 */
export const isDayOfServicePath = (
  pathname: string | undefined = globalThis.location?.pathname,
): boolean =>
  getPathSegments(pathname ?? "").some(
    (segment) =>
      resolveVisitReasonFromSlug(segment) === DigitalVisitReasonEnum.HealthyMouthMovement,
  );

/** The prefix client-side `fetch` calls to this app's API routes must be built from. */
export const getApiBasePath = (): string => {
  const basePath = getBasePath();

  if (basePath.toLowerCase() === PUBLIC_SCHEDULER_BASE_PATH && isDayOfServicePath()) {
    return `${basePath}/${DAY_OF_SERVICE_API_SEGMENT}`;
  }

  return basePath;
};
